/**
 * DeductionsPage
 * Page for viewing all deductions across the organisation for a given period.
 * Pattern: Page as Orchestrator using PageContentArea + PageHeader + PageToolbar
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { AddIcon, PageHeader, Text } from '@billda/design-system';
import { PermissionGate } from '../../../shared/components/gates/PermissionGate';
import { Permissions } from '@billda/permissions-ui';
import { PageContentArea } from '../../../shared/components/layout/PageContentArea';
import { PageHeaderAction } from '../../../shared/components/layout/PageHeaderAction';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { DeductionsListView } from '../components/DeductionsListView';
import { DeductionGroupingMenu } from '../components/DeductionGroupingMenu';
import { PageSnackbar } from '../components/PageSnackbar';
import { CreateDeductionPanelContent } from '../components/panel/CreateDeductionPanelContent';
import { DeductionDetailPanelContent } from '../components/panel/DeductionDetailPanelContent';
import { ResizableHandle, SidePanel } from '../../../shared/components/panel';
import { PeriodSelector } from '../components/PeriodSelector';
import { usePotPeriodState } from '../hooks/usePotPeriodState';
import { usePeriodNavigation } from '../hooks/usePeriodNavigation';
import { useGetPotPeriods } from '../hooks/useGetPotPeriods';
import { useGetDeductions } from '../hooks/useGetDeductions';
import { useDeductionsUIStore } from '../stores/deductionsUIStore';
import { formatCurrency } from '../../../shared/utils/currency';
import type { AdminDeductionRow } from '@billda/api';
import { summariseDeductions } from '../utils/summariseDeductions';

type PanelType = 'create' | 'detail';

export default function DeductionsPage() {
  const [yearsToLoad, setYearsToLoad] = useState(3);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [panelType, setPanelType] = useState<PanelType>('create');
  const [selectedDeduction, setSelectedDeduction] =
    useState<AdminDeductionRow | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  // 1. URL state — period selection
  const { selectedPeriodId: urlPeriodId, setPeriodId } = usePotPeriodState();

  // 1a. Local period state for immediate updates (URL updates are async)
  const [localPeriodId, setLocalPeriodId] = useState<string | null>(null);
  const selectedPeriodId = localPeriodId ?? urlPeriodId;

  useEffect(() => {
    if (urlPeriodId && urlPeriodId !== localPeriodId) {
      setLocalPeriodId(urlPeriodId);
    }
  }, [urlPeriodId, localPeriodId]);

  const handlePeriodChange = useCallback(
    (periodId: string) => {
      setLocalPeriodId(periodId);
      setPeriodId(periodId);
    },
    [setPeriodId]
  );

  // 2. Fetch period data
  const { data: periodData } = useGetPotPeriods({ yearsToLoad });

  const handleLoadMoreYears = useCallback(() => {
    setYearsToLoad(prev => prev + 3);
  }, []);

  // 3. Period navigation hook
  const {
    effectivePeriodId,
    validatedPeriodId,
    handlePrev,
    handleNext,
    canGoPrev,
    canGoNext,
    periodsByYear,
    selectedPeriodLabel,
    selectedPeriodShortLabel,
  } = usePeriodNavigation({
    periodData,
    selectedPeriodId,
    onPeriodChange: handlePeriodChange,
  });

  // 4. Fetch deductions data — use validatedPeriodId to avoid 404s with stale IDs
  const { deductions, isLoading, error } = useGetDeductions(validatedPeriodId);

  // 5. Computed values
  const deductionSummary = useMemo(
    () => summariseDeductions(deductions),
    [deductions]
  );

  // 6. Handlers
  const handleAddDeduction = useCallback(() => {
    setPanelType('create');
    setSelectedDeduction(null);
    setIsPanelOpen(true);
  }, []);

  const handleDeductionClick = useCallback((deduction: AdminDeductionRow) => {
    setSelectedDeduction(deduction);
    setPanelType('detail');
    setIsPanelOpen(true);
  }, []);

  const handlePanelClose = useCallback(() => {
    setIsPanelOpen(false);
    setSelectedDeduction(null);
  }, []);

  const handleCreateSuccess = useCallback(() => {
    setIsPanelOpen(false);
    setSelectedDeduction(null);
    setSnackbarMessage('Deduction created successfully');
  }, []);

  const handleDetailSuccess = useCallback((message: string) => {
    setIsPanelOpen(false);
    setSelectedDeduction(null);
    setSnackbarMessage(message);
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbarMessage(null);
  }, []);

  // 7. Reset store on unmount
  useEffect(() => {
    return () => {
      useDeductionsUIStore.getState().reset();
    };
  }, []);

  // 8. Panel title
  const panelTitle = useMemo(() => {
    if (panelType === 'create') {
      return 'Add Deduction';
    }
    return 'Deduction Details';
  }, [panelType]);

  // 9. Panel content
  const panelContent = useMemo(() => {
    if (!isPanelOpen) {
      return undefined;
    }
    return (
      <>
        <ResizableHandle storageKey="deduction-panel" />
        <SidePanel
          open
          onClose={handlePanelClose}
          title={panelTitle}
          storageKey="deduction-panel"
        >
          {panelType === 'create' ? (
            <CreateDeductionPanelContent
              onSuccess={handleCreateSuccess}
              onCancel={handlePanelClose}
            />
          ) : selectedDeduction ? (
            <DeductionDetailPanelContent
              deduction={selectedDeduction}
              onSuccess={handleDetailSuccess}
            />
          ) : null}
        </SidePanel>
      </>
    );
  }, [
    isPanelOpen,
    panelType,
    panelTitle,
    selectedDeduction,
    handlePanelClose,
    handleCreateSuccess,
    handleDetailSuccess,
  ]);

  // 10. Header title with count and total
  const headerTitle = useMemo(() => {
    if (deductionSummary.count === 0) {
      return 'Deductions (0)';
    }
    return (
      <>
        Deductions ({deductionSummary.count}){' '}
        <Text as="span" variant="inherit" color="secondary">
          Total: {formatCurrency(deductionSummary.totalAmount)}
        </Text>
      </>
    );
  }, [deductionSummary]);

  return (
    <PageContentArea isPanelOpen={isPanelOpen} panel={panelContent}>
      <PageHeader
        title={headerTitle}
        actions={
          <PermissionGate permission={Permissions.Deductions.Manage}>
            <PageHeaderAction
              label="Add Deduction"
              onClick={handleAddDeduction}
              icon={<AddIcon />}
              data-testid="add-deduction-button"
            />
          </PermissionGate>
        }
        data-testid="deductions-header"
      />

      <PageToolbar
        actions={<DeductionGroupingMenu />}
        tabs={
          <PeriodSelector
            label={selectedPeriodLabel}
            shortLabel={selectedPeriodShortLabel}
            periodsByYear={periodsByYear}
            selection={{
              effectivePeriodId,
              onPeriodChange: handlePeriodChange,
            }}
            navigation={{
              onPrev: handlePrev,
              onNext: handleNext,
              canGoPrev,
              canGoNext,
            }}
            pagination={{
              hasMoreYears: periodData?.hasMoreYears,
              onLoadMoreYears: handleLoadMoreYears,
            }}
          />
        }
        data-testid="deductions-toolbar"
      />

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <DeductionsListView
          deductions={deductions}
          isLoading={isLoading}
          error={error}
          onDeductionClick={handleDeductionClick}
        />
      </div>

      <PageSnackbar
        snackbar={{
          open: !!snackbarMessage,
          message: snackbarMessage ?? '',
          severity: 'success',
        }}
        onClose={handleCloseSnackbar}
      />
    </PageContentArea>
  );
}
